import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { devError } from "@/lib/log";
import { api, SUCURSAL_KEY, setSessionToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest
  const [sucursalId, setSucursalIdState] = useState(
    typeof window !== "undefined" ? localStorage.getItem(SUCURSAL_KEY) || "" : ""
  );

  const setSucursalId = useCallback((id) => {
    if (id) localStorage.setItem(SUCURSAL_KEY, id);
    else localStorage.removeItem(SUCURSAL_KEY);
    setSucursalIdState(id || "");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Store Bearer token as fallback for browsers blocking third-party cookies.
    if (data.access_token) setSessionToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      devError("logout error", e);
    }
    setSessionToken(null);
    setUser(false);
    setSucursalId("");
    window.location.href = "/login";
  }, [setSucursalId]);

  return (
    <AuthCtx.Provider value={{ user, sucursalId, setSucursalId, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
